import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid';

export type ApiFilterOperator =
  | 'eq'
  | 'neq'
  | 'contains'
  | 'startsWith'
  | 'endsWith'
  | 'in'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte';

export type ApiFilter = { field: string; operator: ApiFilterOperator; value: unknown };

export type ApiSort = { field: string; direction: 'asc' | 'desc' };
export type GridQueryInput = {
  page: number;
  pageSize: number;
  sorts: ApiSort[];
  filters: ApiFilter[];
  search?: string;
};

export type GridQueryResult<T> = { items: T[]; total: number; page: number; pageSize: number };

export const buildGridQueryInput = ({
  page,
  pageSize,
  sorts,
  filters,
  search,
}: GridQueryInput): GridQueryInput => ({ page, pageSize, sorts, filters, search });

const mapFilterOperator = (operator: string | null | undefined): ApiFilterOperator | null => {
  if (!operator) return null;
  switch (operator) {
    case 'equals':
    case 'is':
    case '=':
      return 'eq';
    case 'not':
    case '!=':
      return 'neq';
    case 'contains':
      return 'contains';
    case 'startsWith':
      return 'startsWith';
    case 'endsWith':
      return 'endsWith';
    case 'isAnyOf':
      return 'in';
    case 'after':
    case '>':
      return 'gt';
    case 'onOrAfter':
    case '>=':
      return 'gte';
    case 'before':
    case '<':
      return 'lt';
    case 'onOrBefore':
    case '<=':
      return 'lte';
    default:
      return null;
  }
};

type GridFilterValue =
  | string
  | number
  | boolean
  | Date
  | null
  | undefined
  | Array<string | number | boolean | Date | null | undefined>;

const isGridFilterPrimitive = (
  value: unknown,
): value is string | number | boolean | Date | null | undefined => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return true;
  }
  return value instanceof Date;
};

const resolveGridFilterValue = (
  value: GridFilterModel['items'][number]['value'],
): GridFilterValue => {
  const resolved = value as unknown;
  if (isGridFilterPrimitive(resolved)) return resolved;
  if (Array.isArray(resolved)) {
    const normalized = resolved
      .map((entry) => (isGridFilterPrimitive(entry) ? entry : null))
      .filter(
        (entry): entry is string | number | boolean | Date => entry !== null && entry !== undefined,
      );
    return normalized.length > 0 ? normalized : null;
  }
  return null;
};

const normaliseFilterValue = (value: GridFilterValue): ApiFilter['value'] => {
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map((entry) =>
      entry instanceof Date ? entry.toISOString() : (entry as ApiFilter['value']),
    );
  }
  return value as ApiFilter['value'];
};

const isFilterValuePresent = (value: GridFilterValue): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

export const mapGridFilters = (filterModel: GridFilterModel): ApiFilter[] => {
  return filterModel.items
    .map((item) => {
      const operator = mapFilterOperator(item.operator);
      if (!operator) return null;
      const value = resolveGridFilterValue(item.value);
      const hasValue =
        isFilterValuePresent(value) ||
        (value === null && (operator === 'eq' || operator === 'neq'));
      if (!item.field || !hasValue) return null;
      return { field: item.field, operator, value: normaliseFilterValue(value) };
    })
    .filter((item): item is ApiFilter => Boolean(item));
};

export const mapGridSorts = (sortModel: GridSortModel): ApiSort[] =>
  sortModel.map((s) => ({ field: s.field, direction: s.sort === 'desc' ? 'desc' : 'asc' }));
